import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { base58_to_binary } from "base58-js";
import Heading from "components/Typography/Header";
import Button from "components/Button";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import swal from 'sweetalert';
import { createPayouts, reset, getLimitUsage } from "redux/reseller/payout/payoutSlice";
import { profile } from "redux/reseller/profile/profileSlice";
import { useUUID } from "hooks/useUUID";
import { useCustomSettings } from 'hooks/useCustomSettings';

const CreatePayoutsContainer = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [trigger, setTrigger] = useState(0);
  const [lastFormValues, setLastFormValues] = useState(null);
  const uuid = useUUID(trigger);
  
  // Get the type parameter from URL (iban or crypto)
  const payoutType = searchParams.get('type') || 'iban';
  const {
    isCreatePayoutSuccess,
    isCreatePayoutError,
    isCreatePayoutLoading,
    limitUsage,
    isLimitUsageLoading,
    message
  } = useSelector((state) => state.resellerPayouts);

  const { profile: profileData } = useSelector(state => state.resellerProfile)
  
  // Get settings data for commission calculations
  const { data: settingsData } = useSelector(state => state.resellerSettings);
  const keyTextMapping = {}; // Assuming this might be needed for the hook
  const payoutFeeFixed = useCustomSettings(settingsData, keyTextMapping, 'payout_fee_fixed');

  // Format monetary values with Turkish locale
  const formatMoney = (amount) => {
    if (!amount && amount !== 0) return '0,00';
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Format limit usage display
  const formatLimitUsage = () => {
    if (!limitUsage || !profileData?.daily_autopayout_limit || profileData.daily_autopayout_limit <= 0) {
      return null;
    }

    const dailyLimit = parseFloat(profileData.daily_autopayout_limit);
    const usedAmount = parseFloat(limitUsage.used_amount || 0);
    const remainingAmount = parseFloat(limitUsage.remaining_amount || 0);

    return {
      dailyLimit: Math.floor(dailyLimit),
      usedAmount: Math.floor(usedAmount),
      remainingAmount: Math.floor(remainingAmount)
    };
  };

  // Check if amount is suitable for autopayout
  const isAutoPayoutSuitable = (amount) => {
    if (!amount || !limitUsage || !profileData?.daily_autopayout_limit || profileData.daily_autopayout_limit <= 0) {
      return false;
    }
    
    const netAmount = parseFloat(amount);
    const remainingAmount = parseFloat(limitUsage.remaining_amount || 0);
    
    return netAmount <= remainingAmount;
  };

  // Calculate commission based on backend logic - user enters net amount
  const calculateCommission = useMemo(() => {
    return (netAmount, counterpartyPaysShare = 0) => {
      if (!netAmount || !profileData?.payout_fee) {
        return {
          feeAmount: 0,
          grossAmount: netAmount || 0,
          netAmount: netAmount || 0,
          actualNetAmount: netAmount || 0,
          counterpartyFeeAmount: 0,
          resellerFeeAmount: 0
        };
      }

      const originalNetAmount = parseFloat(netAmount);
      const commissionRate = parseFloat(profileData.payout_fee);
      const fixedFee = parseFloat(payoutFeeFixed.value) || 0;
      const counterpartyShare = parseFloat(counterpartyPaysShare) || 0;
      
      const commissionRateDecimal = commissionRate / 100; // Convert percentage to decimal
      
      if (counterpartyShare > 0) {
        // Backend logic with counterparty payment
        // Base variable fee (without fixed)
        const variableFee = originalNetAmount * commissionRateDecimal;
        
        // Counterparty pays part of variable fee
        const counterpartyFeeAmount = variableFee * counterpartyShare;
        let resellerVariableFee = variableFee - counterpartyFeeAmount;
        // second reseller fee stage - %3.5 da %100 bayiye ile 100,000 çekerse 100,000 TL çıkacak, normalde 103,626 ödeyecekti, bu formülde 3500 ödüyor, 3500/(1-0.035), toplamı 103,626 TL
        resellerVariableFee += resellerVariableFee / (1 - commissionRateDecimal);
        
        // Actual sent amount (reduced by counterparty's share)
        const actualNetAmount = originalNetAmount - counterpartyFeeAmount;
        
        // Fee recorded for reseller (remaining variable + fixed)
        const resellerFeeAmount = resellerVariableFee + fixedFee;
        
        // Total to charge from reseller
        const grossAmount = originalNetAmount + resellerFeeAmount;
        
        return {
          feeAmount: parseFloat((variableFee + fixedFee).toFixed(2)), // Total fee for display
          grossAmount: parseFloat(grossAmount.toFixed(2)),
          netAmount: parseFloat(originalNetAmount.toFixed(2)), // Original input amount
          actualNetAmount: parseFloat(actualNetAmount.toFixed(2)), // Amount actually sent to bank
          counterpartyFeeAmount: parseFloat(counterpartyFeeAmount.toFixed(2)),
          resellerFeeAmount: parseFloat(resellerFeeAmount.toFixed(2))
        };
      } else {
        // Original logic when counterparty doesn't pay
        const feeAmount = originalNetAmount * commissionRateDecimal / (1 - commissionRateDecimal) + fixedFee;
        const grossAmount = originalNetAmount + feeAmount;

        return {
          feeAmount: parseFloat(feeAmount.toFixed(2)),
          grossAmount: parseFloat(grossAmount.toFixed(2)),
          netAmount: parseFloat(originalNetAmount.toFixed(2)),
          actualNetAmount: parseFloat(originalNetAmount.toFixed(2)),
          counterpartyFeeAmount: 0,
          resellerFeeAmount: parseFloat(feeAmount.toFixed(2))
        };
      }
    };
  }, [profileData?.payout_fee, payoutFeeFixed]);

  // Validation function for Tron addresses using base58 check decode
  const validateTronAddress = (address) => {
    try {
      if (!address || !address.startsWith('T')) {
        return false;
      }
      // Decode the address using base58 check
      const decoded = base58_to_binary(address);
      // Tron addresses should decode to 25 bytes (21 bytes + 4 bytes checksum)
      return decoded && decoded.length === 25;
    } catch (error) {
      return false;
    }
  };

  const createPayoutForm = useMemo(() => {
    if (payoutType === 'iban') {
      return Yup.object().shape({
        iban: Yup.string()
          .required("IBAN Zorunludur")
          .test('iban-format', 'IBAN TR ile başlamalı ve 26 karakter olmalıdır', function(value) {
            if (!value) return false;
            const cleanValue = value.replace(/\s/g, '');
            return cleanValue.toUpperCase().startsWith('TR') && cleanValue.length === 26;
          }),
        holder_name: Yup.string()
          .required("Hesap Sahibi Zorunludur")
          .test('no-digits', 'Hesap sahibi adında rakam bulunamaz', function(value) {
            if (!value) return false;
            return !/\d/.test(value);
          }),
        tckn: Yup.string()
          .test('tckn-format', 'TCKN 11 haneli, VKN 10 haneli olmalıdır', function(value) {
            if (!value || value.trim() === '') return true; // Allow empty
            const cleanValue = value.replace(/\s/g, ''); // Remove spaces
            return /^\d{10}$/.test(cleanValue) || /^\d{11}$/.test(cleanValue); // 10 or 11 digits
          }),
        net_amount_try: Yup.number().required("Tutar Zorunludur"),
        counterparty_pays_share: Yup.number().min(0).max(1),
      });
    } else {
      // Crypto validation
      return Yup.object().shape({
        iban: Yup.string()
          .required("Cüzdan Adresi Zorunludur")
          .test('tron-address', 'Geçerli bir Tron (TRX / TRC20) cüzdan adresi giriniz', function(value) {
            if (!value) return false;
            return validateTronAddress(value.trim());
          }),
        holder_name: Yup.string()
          .required("Cüzdan Sahibi Adı Zorunludur")
          .test('no-digits', 'Cüzdan sahibi adında rakam bulunamaz', function(value) {
            if (!value) return false;
            return !/\d/.test(value);
          }),
        tckn: Yup.string()
          .test('tckn-format', 'TCKN 11 haneli, VKN 10 haneli olmalıdır', function(value) {
            if (!value || value.trim() === '') return true; // Allow empty
            const cleanValue = value.replace(/\s/g, ''); // Remove spaces
            return /^\d{10}$/.test(cleanValue) || /^\d{11}$/.test(cleanValue); // 10 or 11 digits
          }),
        net_amount_try: Yup.number().required("Tutar Zorunludur"),
        counterparty_pays_share: Yup.number().min(0).max(1),
      });
    }
  }, [payoutType]);

  const [resetFormFunc, setResetFormFunc] = useState({});

  useEffect(() => {
    dispatch(profile());
    dispatch(getLimitUsage());
  }, [dispatch]);

  useEffect(() => {
    if (isCreatePayoutLoading) {
      // Loading state
    } else if (isCreatePayoutError) {
      if(message && !message.toString().includes("undefined"))
        setTrigger((prev) => prev + 1);

      if (message.net_amount_try) {
        toast.error(message.net_amount_try);
      } else if (message.iban) {
        toast.error(message.iban);
      } else
        toast.error(message);
      
    } else if (message && message.need_confirm) {
      setTrigger((prev) => prev + 1);
      const el = document.createElement("div");
      el.innerHTML = message.message;
      swal({
        title: "Çekim Onayı",
        content: {
          element: el
        },
        icon: "info",
        buttons: ["İptal", "Onayla"]
      })
      .then(confirmed => {
        if (confirmed) {
          const confirmPayoutData = {
            iban: lastFormValues.iban,
            holder_name: lastFormValues.holder_name,
            net_amount_try: lastFormValues.net_amount_try.toString(),
            tckn: lastFormValues.tckn,
            nonce: uuid+"-confirm",
            confirm: true
          };
          
          // Add counterparty payment share if it was selected (only for IBAN)
          if (lastFormValues.counterparty_pays_share > 0 && payoutType === 'iban') {
            confirmPayoutData.counterparty_pays_share = lastFormValues.counterparty_pays_share.toString();
          }
          
          dispatch(createPayouts(confirmPayoutData));
        }
      });
    } else if (isCreatePayoutSuccess) {
      setTrigger((prev) => prev + 1);
      toast.success(message?.message ?? "Talebiniz Alınmıştır.");
      if (resetFormFunc.resetForm) {
        resetFormFunc.resetForm();
      }
      //navigate("/reseller/payouts");
    }
    return () => {
      dispatch(reset());
    };
  }, [isCreatePayoutError, isCreatePayoutSuccess, isCreatePayoutLoading, message, dispatch, lastFormValues, uuid]);

  const handleSubmit = async (values, {resetForm}) => {
    setResetFormFunc({resetForm: resetForm});
    setLastFormValues(values);
    
    const payoutData = {
      iban: values.iban,
      holder_name: values.holder_name,
      net_amount_try: values.net_amount_try.toString(),
      tckn: values.tckn,
      nonce: uuid,
      confirm: false
    };
    
    // Add counterparty payment share if selected (only for IBAN payouts)
    if (values.counterparty_pays_share > 0 && payoutType === 'iban') {
      payoutData.counterparty_pays_share = values.counterparty_pays_share.toString();
    }
    
    dispatch(createPayouts(payoutData));
  };


  return (
    <div className="p-4 ">
      <Heading className="text-white-100" level={5}>
        Para Çekme Talebi
      </Heading>
      <div className="p-4 bg-gray-900 my-4 rounded-xl min-h-screen">
        <div className="grid grid-cols-1  gap-4">
          <div className="p-4 bg-black md:h-screen">
            <Formik
              initialValues={{
                iban: "",
                holder_name: "",
                tckn: "",
                net_amount_try: "",
                counterparty_pays_share: 0,
              }}
              validationSchema={createPayoutForm}
              onSubmit={handleSubmit}
            >
              {({ errors, touched, setFieldValue, values }) => {
                const commissionInfo = calculateCommission(values.net_amount_try, values.counterparty_pays_share);
                const currentBalance = parseFloat(profileData?.balance_try || 0);
                const finalBalance = currentBalance - commissionInfo.grossAmount;
                
                // Predefined options for counterparty payment
                const counterpartyOptions = [
                  { label: "Bayiden kes", value: 0, description: "Tüm komisyon bayiden kesilir" },
                  { label: "Yarısı karşıdan kes", value: 0.5, description: "Değişken komisyonun %50'si karşıdan kesilir" },
                  { label: "Tümü karşıdan kes", value: 1.0, description: "Değişken komisyonun tümü karşıdan kesilir" }
                ];
                
                return (
                  <Form className="space-y-4 max-w-[350px]">
                    <div className="w-full">
                      <div className="text-white-100 font-medium mb-4">Hesabınızda Bulunan Bakiye: {formatMoney(profileData?.balance_try)} TL</div>
                      <span className="text-white-100 font-medium">
                        {payoutType === 'iban' ? 'IBAN' : 'Cüzdan Adresi (Tron/TRC20)'}
                      </span>
                      <Field
                        name="iban"
                        type="text"
                        className="w-full font-medium bg-white-100 rounded-lg appearance-none focus:outline-none  p-2 mt-2"
                        placeholder={payoutType === 'iban' ? 'TR00 0000 0000 0000 0000 0000 00' : 'Cüzdan Adresi'}
                      />
                      <ErrorMessage
                        name="iban"
                        component="div"
                        className="text-red-500"
                      />
                    </div>
                    <div className="w-full">
                      <span className="text-white-100 font-medium">
                        {payoutType === 'iban' ? 'Hesap Sahibi Ad Soyad' : 'Cüzdan Sahibi Ad Soyad'}
                      </span>
                      <Field
                        name="holder_name"
                        type="text"
                        className="w-full font-medium bg-white-100 rounded-lg appearance-none focus:outline-none  p-2 mt-2"
                        placeholder={payoutType === 'iban' ? 'Hesap sahibinin adı soyadı' : 'Cüzdan sahibinin adı soyadı'}
                      />
                      <ErrorMessage
                        name="holder_name"
                        component="div"
                        className="text-red-500"
                      />
                    </div>
                    <div className="w-full">
                      <span className="text-white-100 font-medium">
                        TCKN/VKN {payoutType === 'crypto' && '(Opsiyonel)'}
                      </span>
                      <Field
                        name="tckn"
                        type="text"
                        className="w-full font-medium bg-white-100 rounded-lg appearance-none focus:outline-none  p-2 mt-2"
                        placeholder="TC Kimlik No veya Vergi Kimlik No"
                      />
                      <ErrorMessage
                        name="tckn"
                        component="div"
                        className="text-red-500"
                      />
                    </div>
                    <div className="w-full">
                      <span className="text-white-100 font-medium">
                      Alacağınız Tutar (TL)
                      </span>
                      <Field
                        name="net_amount_try"
                        type="number"
                        className="w-full font-medium bg-white-100 rounded-lg appearance-none focus:outline-none  p-2 mt-2"
                        placeholder={'Almak istediğiniz net TL tutarı'}
                      />
                      <ErrorMessage
                        name="net_amount_try"
                        component="div"
                        className="text-red-500"
                      />
                    </div>

                    {/* Counterparty Payment Selection */}
                    {payoutType === 'iban' && (
                      <div className="w-full">
                        <span className="text-white-100 font-medium mb-3 block">Komisyon Ödemesi</span>
                        <div className="space-y-2">
                          {counterpartyOptions.map((option) => (
                            <label
                              key={option.value}
                              className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                                values.counterparty_pays_share === option.value
                                  ? 'border-primary bg-primary/10'
                                  : 'border-gray-600 bg-gray-800 hover:bg-gray-700'
                              }`}
                            >
                              <input
                                type="radio"
                                name="counterparty_pays_share"
                                value={option.value}
                                checked={values.counterparty_pays_share === option.value}
                                onChange={(e) => setFieldValue('counterparty_pays_share', parseFloat(e.target.value))}
                                className="sr-only"
                              />
                              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center mr-3 ${
                                values.counterparty_pays_share === option.value
                                  ? 'border-primary'
                                  : 'border-gray-400'
                              }`}>
                                {values.counterparty_pays_share === option.value && (
                                  <div className="w-2 h-2 rounded-full bg-primary"></div>
                                )}
                              </div>
                              <div>
                                <div className="text-white-100 font-medium text-sm">{option.label}</div>
                                <div className="text-gray-400 text-xs">{option.description}</div>
                              </div>
                            </label>
                          ))}
                        </div>
                        <div className="mt-2 text-xs text-gray-400">
                          * {payoutFeeFixed.value} TL sabit gönderim ücreti her durumda bayiden kesilir
                        </div>
                      </div>
                    )}

                    {/* Commission and Rate Information Card - Visible only when no amount entered */}
                    {(!values.net_amount_try || parseFloat(values.net_amount_try) <= 0) && (
                      <div className="bg-gray-800 p-4 rounded-lg space-y-2 border border-gray-700">
                      <div className="text-white-100 font-medium text-sm mb-2">Ücret Bilgileri</div>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between text-gray-300">
                          <span>Komisyon Oranı:</span>
                          <span className="text-white-100 font-medium">{profileData?.payout_fee || 0}% + {payoutFeeFixed.value || 0} TL</span>
                        </div>
                        {payoutType === 'crypto' && profileData?.crypto_payout_rate_offset && parseFloat(profileData.crypto_payout_rate_offset) > 0 && (
                          <div className="flex justify-between text-blue-400">
                            <span>USDT Kuru:</span>
                            <span className="font-medium">{formatMoney(profileData.crypto_payout_rate_offset)} TL</span>
                          </div>
                        )}
                      </div>
                    </div>
                    )}

                    {values.net_amount_try && parseFloat(values.net_amount_try) > 0 && (
                      <div className="bg-gray-800 p-4 rounded-lg space-y-2">
                        <div className="text-white-100 font-medium text-sm">Çekim Özeti</div>
                        <div className="space-y-1 text-sm">
                          {values.counterparty_pays_share > 0 && payoutType === 'iban' ? (
                            <>
                              <div className="flex justify-between text-gray-300">
                                <span>Talep Edilen Tutar:</span>
                                <span>{formatMoney(values.net_amount_try)} TL</span>
                              </div>
                              <div className="flex justify-between text-green-400">
                                <span>Bankaya Gönderilecek:</span>
                                <span>{formatMoney(commissionInfo.actualNetAmount)} TL</span>
                              </div>
                              <div className="border-t border-gray-600 pt-1 space-y-1">
                                <div className="text-xs text-gray-400 mb-1">Komisyon Dağılımı:</div>
                                <div className="flex justify-between text-gray-300 text-xs">
                                  <span>• Karşı taraftan kesilen:</span>
                                  <span>{formatMoney(commissionInfo.counterpartyFeeAmount)} TL</span>
                                </div>
                                <div className="flex justify-between text-gray-300 text-xs">
                                  <span>• Bayiden kesilen:</span>
                                  <span>{formatMoney(commissionInfo.resellerFeeAmount)} TL</span>
                                </div>
                              </div>
                              <div className="border-t border-gray-600 pt-1">
                                <div className="flex justify-between text-white-100 font-medium">
                                  <span>Hesabınızdan Kesilecek Toplam:</span>
                                  <span>{formatMoney(commissionInfo.grossAmount)} TL</span>
                                </div>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="flex justify-between text-gray-300">
                                <span>Net Alacağınız Tutar:</span>
                                <span>{formatMoney(values.net_amount_try)} TL</span>
                              </div>
                              <div className="flex justify-between text-gray-300">
                                <span>Komisyon ({profileData?.payout_fee}% + {payoutFeeFixed.value} TL):</span>
                                <span>{formatMoney(commissionInfo.feeAmount)} TL</span>
                              </div>
                              <div className="border-t border-gray-600 pt-1">
                                <div className="flex justify-between text-white-100 font-medium">
                                  <span>Hesabınızdan Kesilecek Toplam:</span>
                                  <span>{formatMoney(commissionInfo.grossAmount)} TL</span>
                                </div>
                              </div>
                            </>
                          )}
                          {payoutType === 'crypto' && profileData?.crypto_payout_rate_offset && parseFloat(profileData.crypto_payout_rate_offset) > 0 ? (
                            <>
                              <div className="border-t border-gray-600 pt-2 mt-2">
                                <div className="flex justify-between text-blue-400">
                                  <span>USDT Kuru:</span>
                                  <span>{formatMoney(profileData.crypto_payout_rate_offset)} TL</span>
                                </div>
                              </div>
                              <div className="flex justify-between text-green-400 font-medium">
                                <span>Alacağınız USDT:</span>
                                <span>{formatMoney(parseFloat(values.net_amount_try) / parseFloat(profileData.crypto_payout_rate_offset))} USDT</span>
                              </div>
                            </>
                          ) : null}

                         {/* Auto Payout Indicator - HIDDEN as requested */}
                         {values.net_amount_try && parseFloat(values.net_amount_try) > 0 && formatLimitUsage() && (
                           <div className="mt-2" style={{display: 'none'}}>
                             {isAutoPayoutSuitable(values.net_amount_try) ? (
                               <div className="pt-2 mt-3">
                                 <div className="flex items-center text-green-400 text-xs">
                                   <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                     <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                   </svg>
                                   Otomatik gönderilecek
                               </div>
                               <div className="text-gray-300 text-xs">
                                 (Günlük kalan: <span className="text-green-400 font-medium">{formatLimitUsage().remainingAmount.toLocaleString('tr-TR')} TL</span> / Toplam: <span className="text-orange-500 font-medium">{formatLimitUsage().dailyLimit.toLocaleString('tr-TR')} TL</span>)
                               </div></div>
                             ) : null}
                           </div>
                         )}
                       </div>
                     </div>
                   )}

                    <Button
                      type="submit"
                      size="small"
                      loading={isCreatePayoutLoading}
                      disabled={isCreatePayoutLoading || (values.net_amount_try && finalBalance < 0)}
                      className="bg-primary hover:bg-primary-dark text-white-100 rounded-lg w-full py-2 mt-4"
                    >
                      {payoutType === 'iban' ? 'Banka Çekim Talebi Oluştur' : 'Kripto Çekim Talebi Oluştur'}
                    </Button>

                    {values.net_amount_try && parseFloat(values.net_amount_try) > 0 && (
                      <div className="flex flex-col space-y-2 mt-4">
                        <span className={`block text-sm font-medium ${finalBalance >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          İşlem Sonu Bakiyeniz: {formatMoney(finalBalance)} TL
                        </span>
                        {finalBalance < 0 && (
                          <span className="text-red-400 text-xs">
                            Yetersiz bakiye! Çekim için {formatMoney(Math.abs(finalBalance))} TL eksik.
                          </span>
                        )}
                      </div>
                    )}
                  </Form>
                );
              }}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePayoutsContainer;
